import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, Heart, ExternalLink, ScanLine, Smartphone, Copy, Check } from 'lucide-react';
import PageHero from '../components/PageHero';

const BASE = 'https://www.seotechexperts.in/testserver/tes/wp-content/uploads/2026';

// UN SDG portrait-format tiles — official E-Goal cards (taller rectangular tiles)
const UN_SDGS = [
  { num: 4,  label: 'Quality Education',                     color: '#C5192D' },
  { num: 7,  label: 'Affordable and Clean Energy',           color: '#FCC30B' },
  { num: 8,  label: 'Decent Work and Economic Growth',       color: '#A21942' },
  { num: 9,  label: 'Industry, Innovation & Infrastructure', color: '#FD6925' },
  { num: 11, label: 'Sustainable Cities & Communities',      color: '#FD9D24' },
  { num: 13, label: 'Climate Action',                        color: '#3F7E44' },
  { num: 15, label: 'Life on Land',                          color: '#56C02B' },
  { num: 17, label: 'Partnerships for the Goals',            color: '#19486A' },
];

const TREE_GALLERY = [
  { src: `${BASE}/02/6-1.jpg`,   caption: 'Tree Plantation Drive 2024' },
  { src: `${BASE}/02/7-1.jpg`,   caption: 'Sapling Planting' },
  { src: `${BASE}/02/8-1.jpg`,   caption: 'Green Initiative' },
  { src: `${BASE}/02/1-3.jpg`,   caption: 'Plantation Activity' },
  { src: `${BASE}/02/2-2.jpg`,   caption: 'Tree Plantation 2024' },
  { src: `${BASE}/02/3-2.jpg`,   caption: 'Team Plantation' },
  { src: `${BASE}/02/4-1.jpg`,   caption: 'Green Campus Drive' },
];

const YOGA_GALLERY = [
  { src: `${BASE}/02/1-2.jpeg`,  caption: 'International Yoga Day' },
  { src: `${BASE}/02/2-2.webp`,  caption: 'Yoga Day Celebration' },
  { src: `${BASE}/02/3-3.jpeg`,  caption: 'Team Yoga Session' },
  { src: `${BASE}/02/4-4.jpeg`,  caption: 'Wellness at Work' },
];

export default function CSR() {
  return (
    <main>
      <PageHero eyebrow="Corporate Social Responsibility" title="Tricolite Energy" accent="Sadgati Foundation" accentColor="var(--blue)" image="/img/csr-hero.jpg" desc="Our domains of CSR are aligned with the United Nations Sustainable Development Goals — focusing on Skill Development and Climate Action." />

      {/* Sadgati Foundation — Sadgati LOGO as the image card */}
      <section id="sadgati" className="px-6 md:px-10 lg:px-14 py-20 lg:py-32 bg-[var(--cream)]" data-testid="sadgati-section">
        <div className="max-w-[1500px] mx-auto grid lg:grid-cols-12 gap-14 items-center">

          {/* LEFT: Sadgati Foundation Logo — free, no box */}
          <div className="lg:col-span-5 reveal flex flex-col items-center justify-center">
            <img
              src="/logos/sadgati.png"
              alt="Sadgati Foundation"
              className="w-full max-w-[360px] h-auto object-contain mx-auto"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                const el = document.createElement('div');
                el.style.cssText = 'font-family:Syne,sans-serif;font-weight:800;font-size:2.5rem;color:#f26522;text-align:center;line-height:1.2';
                el.textContent = 'Sadgati Foundation';
                e.currentTarget.parentElement.appendChild(el);
              }}
            />
          </div>

          {/* RIGHT: Sadgati text */}
          <div className="lg:col-span-7 reveal">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--blue)] font-semibold mb-4">Tricolite Energy</div>
            <h2 className="font-display text-[clamp(32px,4vw,58px)] leading-[1.03] font-bold tracking-tight">
              <span className="font-serif-it text-[var(--blue)]">Sadgati Foundation</span>
            </h2>
            <div className="mt-7 space-y-5 text-[16px] md:text-[17px] leading-[1.9] text-[var(--text-mid)]">
              <p>Sadgati Foundation is the social-impact arm of Tricolite Energy. Our domains of CSR are aligned with the <b className="text-[var(--ink)]">United Nations Sustainable Development Goals for 2030</b> — focusing on <b className="text-[var(--ink)]">Skill Development</b> and <b className="text-[var(--ink)]">Climate Action</b>.</p>
              <p>We train and develop construction electricians, supervisors and engineers — giving total quality uniform experience at all our customer sites. By equipping our workforce with the right skills, we directly contribute to creating safer, more efficient, and more sustainable energy infrastructure across India.</p>
              <p>Beyond the workforce, we invest in the planet our work depends on — through tree plantation drives, sustainable site practices and renewable-first project planning.</p>
            </div>
          </div>
        </div>
      </section>

      {/* UN SDG SECTION — portrait-format tiles */}
      <section className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream-deep)]" data-testid="un-sdg-section">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-14 text-center max-w-3xl mx-auto">
            <div className="text-[11px] uppercase tracking-[0.22em] text-[var(--blue)] font-semibold mb-4">Our Global Commitment</div>
            <h2 className="font-display text-[clamp(30px,4vw,58px)] leading-[1.04] font-bold tracking-tight">
              Aligned with the <span className="font-serif-it text-[var(--blue)]">UN Sustainable Development Goals.</span>
            </h2>
            <p className="mt-5 text-[17px] text-[var(--text-mid)] leading-[1.85]">
              Tricolite Energy's CSR initiatives directly contribute to the United Nations 2030 Agenda — the global blueprint for peace and prosperity for people and the planet.
            </p>
          </div>

          {/* Portrait-format SDG tiles — 4 per row on desktop */}
          <div className="grid grid-cols-2 sm:grid-cols-4 md:grid-cols-4 lg:grid-cols-8 gap-4 md:gap-5 mb-10">
            {UN_SDGS.map((sdg, i) => (
              <div
                key={sdg.num}
                className="group reveal flex flex-col items-center cursor-pointer"
                style={{ transitionDelay: `${i * 50}ms` }}
                data-testid={`sdg-goal-${sdg.num}`}
              >
                <div
                  className="relative w-full rounded-2xl overflow-hidden transition-all duration-400 group-hover:scale-[1.04] group-hover:shadow-2xl"
                  style={{ aspectRatio: '4/5' }}
                >
                  <img
                    src={`https://www.un.org/sustainabledevelopment/wp-content/uploads/2019/08/E-Goal-${String(sdg.num).padStart(2, '0')}-2019.png`}
                    alt={`SDG ${sdg.num}: ${sdg.label}`}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      const p = e.currentTarget.parentElement;
                      p.style.backgroundColor = sdg.color;
                      p.style.display = 'flex';
                      p.style.flexDirection = 'column';
                      p.style.alignItems = 'center';
                      p.style.justifyContent = 'center';
                      p.style.gap = '8px';
                      p.style.padding = '16px';
                      p.innerHTML = `<div style="font-weight:800;font-size:3rem;line-height:1;color:white;font-family:Syne,sans-serif">${sdg.num}</div><div style="font-size:0.6rem;text-transform:uppercase;letter-spacing:0.1em;color:white;font-weight:600;text-align:center;line-height:1.3">${sdg.label}</div>`;
                    }}
                  />
                </div>
                <p className="mt-2.5 text-[10px] text-center text-[var(--text-mid)] leading-tight font-medium">{sdg.label}</p>
              </div>
            ))}
          </div>

          <div className="reveal text-center">
            <a
              href="https://sdgs.un.org/goals"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[13px] text-[var(--text-mid)] hover:text-[var(--blue)] transition-colors"
            >
              Learn more about the United Nations SDGs <ExternalLink className="w-3.5 h-3.5"/>
            </a>
          </div>
        </div>
      </section>

      {/* Tree plantation gallery */}
      <section id="trees" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream)]" data-testid="tree-plantation-section">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-12 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--blue)] font-semibold mb-4">Tree Plantations</div>
            <h2 className="font-display text-[clamp(32px,4vw,60px)] leading-[1.02] font-bold tracking-tight">
              Putting roots into the ground, <br/><span className="font-serif-it text-[var(--blue)]">one site at a time.</span>
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[var(--text-mid)] leading-[1.85]">In 2024, Tricolite teams planted saplings across our project sites and offices — small acts, repeated, that compound into measurable change.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {TREE_GALLERY.map((item, i) => (
              <div key={i} className="group aspect-[4/3] rounded-2xl overflow-hidden border border-[var(--line)] relative reveal" style={{ transitionDelay: `${i * 40}ms` }} data-testid={`tree-img-${i}`}>
                <img src={item.src} alt={item.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-[11px] font-medium">{item.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Yoga Day gallery */}
      <section id="yoga" className="px-6 md:px-10 lg:px-14 py-20 lg:py-28 bg-[var(--cream-deep)]" data-testid="yoga-section">
        <div className="max-w-[1500px] mx-auto">
          <div className="reveal mb-12 max-w-3xl">
            <div className="text-[11px] uppercase tracking-[0.28em] text-[var(--blue)] font-semibold mb-4">International Yoga Day</div>
            <h2 className="font-display text-[clamp(32px,4vw,60px)] leading-[1.02] font-bold tracking-tight">
              Excellence in work is <span className="font-serif-it text-[var(--blue)]">yoga.</span>
            </h2>
            <p className="mt-6 text-[16px] md:text-[17px] text-[var(--text-mid)] leading-[1.85]">Every year on 21 June, our teams gather to celebrate International Yoga Day — a reminder that disciplined practice, focus and breath are what turn craft into mastery.</p>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
            {YOGA_GALLERY.map((item, i) => (
              <div key={i} className="group aspect-[4/3] rounded-2xl overflow-hidden border border-[var(--line)] relative reveal" style={{ transitionDelay: `${i * 40}ms` }} data-testid={`yoga-img-${i}`}>
                <img src={item.src} alt={item.caption} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"/>
                <div className="absolute inset-0 bg-gradient-to-t from-[var(--ink)]/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"/>
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-full group-hover:translate-y-0 transition-transform duration-300">
                  <p className="text-white text-[11px] font-medium">{item.caption}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA — Make a contribution */}
      <ContributeCTA />
    </main>
  );
}

function ContributeCTA() {
  const UPI_ID = '9944641619@paytm';
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try { await navigator.clipboard.writeText(UPI_ID); setCopied(true); setTimeout(() => setCopied(false), 2000); } catch { /* ignore */ }
  };
  const upiLink = `upi://pay?pa=${UPI_ID}&pn=Sadgati%20Foundation&cu=INR`;

  return (
    <section className="px-6 md:px-10 lg:px-14 pb-20 bg-[var(--cream-deep)]" data-testid="contribute-section">
      <div className="max-w-[1500px] mx-auto relative overflow-hidden rounded-3xl bg-[var(--orange)] text-white p-8 md:p-14 lg:p-16">
        {/* Decorative layers */}
        <div className="absolute inset-0 noise opacity-60" aria-hidden="true"/>
        <div className="absolute -top-32 -right-32 w-[420px] h-[420px] rounded-full bg-white/10 blur-3xl pointer-events-none" aria-hidden="true"/>
        <div className="absolute -bottom-24 -left-24 w-[360px] h-[360px] rounded-full bg-[var(--ink)]/15 blur-3xl pointer-events-none" aria-hidden="true"/>

        <div className="relative grid lg:grid-cols-[1.1fr_1fr] gap-10 lg:gap-16 items-center">
          {/* LEFT — Headline & supporting copy */}
          <div>
            <div className="inline-flex items-center gap-2 px-3.5 py-1.5 rounded-full bg-white/15 backdrop-blur-sm border border-white/25 text-[11px] uppercase tracking-[0.22em] font-semibold mb-6">
              <Heart className="w-3.5 h-3.5"/> Donate to Sadgati
            </div>
            <h2 className="font-display text-[clamp(28px,3.6vw,46px)] leading-[1.05] font-bold tracking-tight" style={{ color:'#fff' }}>
              Make a contribution<br/>
              <span className="font-serif-it text-white/90">to Sadgati Foundation.</span>
            </h2>
            <p className="mt-6 text-[15px] md:text-[16px] leading-[1.85] text-white/85 max-w-xl">
              Every rupee helps us train one more construction electrician, light up one more rural classroom, and bring clean energy to one more community. Scan the QR with any UPI app to contribute directly to Sadgati Foundation&rsquo;s skill &amp; climate-action initiatives.
            </p>

            {/* Trust strip — UPI apps */}
            <div className="mt-7 flex items-center gap-4">
              <span className="text-[10px] uppercase tracking-[0.22em] text-white/55 font-semibold">Pay via</span>
              <div className="flex items-center gap-2">
                {['GPay','PhonePe','Paytm','BHIM'].map(name => (
                  <span key={name} className="px-2.5 py-1 rounded-full bg-white/12 border border-white/20 text-[11px] font-medium tracking-wide">{name}</span>
                ))}
              </div>
            </div>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <Link to="/contact" className="inline-flex items-center justify-center gap-2 bg-[var(--ink)] hover:bg-black text-white px-7 py-3.5 rounded-full font-medium transition-colors" data-testid="csr-partner-cta">
                Partner with us <ArrowUpRight className="w-4 h-4"/>
              </Link>
              <a href={upiLink} className="inline-flex items-center justify-center gap-2 bg-white text-[var(--orange)] hover:bg-white/90 px-7 py-3.5 rounded-full font-semibold transition-colors" data-testid="csr-upi-deeplink">
                <Smartphone className="w-4 h-4"/> Pay on phone
              </a>
            </div>
          </div>

          {/* RIGHT — QR card */}
          <div className="relative justify-self-center lg:justify-self-end w-full max-w-sm">
            <div className="relative bg-white rounded-[28px] p-7 md:p-8 shadow-[0_30px_60px_-20px_rgba(0,0,0,0.45)]" data-testid="qr-card">
              {/* Scan corners — decorative */}
              <ScanCorner pos="tl"/>
              <ScanCorner pos="tr"/>
              <ScanCorner pos="bl"/>
              <ScanCorner pos="br"/>

              <div className="flex items-center justify-between mb-5">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--orange)] font-bold">Scan to Donate</div>
                  <div className="font-display font-bold text-[18px] text-[var(--ink)] mt-1">Sadgati Foundation</div>
                </div>
                <div className="w-9 h-9 rounded-full bg-[var(--orange)]/10 flex items-center justify-center">
                  <ScanLine className="w-4 h-4 text-[var(--orange)]"/>
                </div>
              </div>

              {/* QR */}
              <div className="relative rounded-2xl border-2 border-dashed border-[var(--orange)]/30 p-3 bg-white">
                <img
                  src="/sadgati-qr.png"
                  alt="Scan this QR to donate to Sadgati Foundation"
                  className="w-full h-auto block"
                  data-testid="sadgati-qr-image"
                  style={{ imageRendering: 'pixelated' }}
                />
              </div>

              {/* UPI ID + copy */}
              <div className="mt-5">
                <div className="text-[10px] uppercase tracking-[0.22em] text-[var(--text-light)] font-semibold mb-2">UPI ID</div>
                <button type="button" onClick={copy} className="w-full flex items-center justify-between gap-3 px-4 py-3 rounded-xl bg-[var(--cream-deep)] border border-[var(--line)] hover:border-[var(--orange)] transition-colors group" data-testid="copy-upi">
                  <span className="font-mono text-[13px] text-[var(--ink)] truncate">{UPI_ID}</span>
                  {copied ? <Check className="w-4 h-4 text-emerald-600 shrink-0"/> : <Copy className="w-4 h-4 text-[var(--text-mid)] group-hover:text-[var(--orange)] shrink-0"/>}
                </button>
                <p className="text-[11px] text-[var(--text-light)] mt-2 leading-relaxed">
                  Donations are tax-deductible under <b className="text-[var(--ink)]">Sec 80G</b>. Email <a href="mailto:csr@tricoliteenergy.in" className="link-u text-[var(--orange)]">csr@tricoliteenergy.in</a> for a receipt.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

// Subtle camera-style scan corners around the QR card
function ScanCorner({ pos }) {
  const map = {
    tl: 'top-3 left-3 border-t-2 border-l-2 rounded-tl-xl',
    tr: 'top-3 right-3 border-t-2 border-r-2 rounded-tr-xl',
    bl: 'bottom-3 left-3 border-b-2 border-l-2 rounded-bl-xl',
    br: 'bottom-3 right-3 border-b-2 border-r-2 rounded-br-xl',
  };
  return <span aria-hidden="true" className={`pointer-events-none absolute w-5 h-5 border-[var(--orange)] ${map[pos]}`}/>;
}
